import React, { useEffect, useState } from "react";
import { Users, FolderOpen, ShieldQuestion, Cpu, CheckCircle2, XCircle, FileText, Receipt, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { StatCard, Card, LoadingBlock, EmptyState, formatDateTime } from "@/components/Common";
import { useI18n, useL } from "@/context/I18nContext";
import api from "@/lib/api";

const COLORS = ["#F5A623", "#00B4D8", "#34D399", "#A78BFA", "#F87171", "#60A5FA", "#FBBF24"];

export default function AdminOverview() {
  const L = useL();
  const { lang } = useI18n();
  const currencyLocale = lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "it-IT";
  const money = (v) => new Intl.NumberFormat(currencyLocale, { style: "currency", currency: "EUR" }).format(v);
  const [stats, setStats] = useState(null);
  const [fin, setFin] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setStats(false));
    api.get("/admin/financial/stats").then(({ data }) => setFin(data)).catch(() => {});
  }, []);
  if (stats === null) return <LoadingBlock />;
  if (stats === false) return <EmptyState title={L("Errore nel caricamento", "Loading error", "Fehler beim Laden")} />;

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl font-medium text-white">{L("Panoramica operativa", "Operational overview", "Betriebsübersicht")}</h2>

      {fin && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={CheckCircle2} label={L("Saldi verificati", "Verified balances", "Verifizierte Salden")} value={money(fin.total_verified_balances)} tone="green" testid="fin-total-balances" />
          <StatCard icon={FileText} label={L("Depositi in sospeso", "Pending deposits", "Ausstehende Einzahlungen")} value={fin.pending_deposits} tone="gold" />
          <StatCard icon={FileText} label={L("Prelievi in sospeso", "Pending withdrawals", "Ausstehende Auszahlungen")} value={fin.pending_withdrawals} tone="gold" />
          <StatCard icon={Receipt} label={L("Commissioni raccolte", "Collected commissions", "Eingezogene Provisionen")} value={money(fin.collected_commissions)} tone="cyan" />
          <StatCard icon={CheckCircle2} label={L("Depositi confermati", "Confirmed deposits", "Bestätigte Einzahlungen")} value={fin.confirmed_deposits} tone="slate" />
          <StatCard icon={CheckCircle2} label={L("Prelievi completati", "Completed withdrawals", "Abgeschlossene Auszahlungen")} value={fin.completed_withdrawals} tone="green" />
          <StatCard icon={XCircle} label={L("Transazioni fallite", "Failed transactions", "Fehlgeschlagene Transaktionen")} value={fin.failed_transactions} tone="red" />
          <StatCard icon={ShieldQuestion} label={L("Da revisionare", "To review", "Zu prüfen")} value={fin.manual_review} tone="gold" />
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label={L("Clienti registrati", "Registered clients", "Registrierte Kunden")} value={stats.total_clients} tone="cyan" testid="stat-clients" />
        <StatCard icon={Activity} label={L("Nuovi lead", "New leads", "Neue Leads")} value={stats.new_leads} tone="gold" testid="stat-leads" />
        <StatCard icon={FolderOpen} label={L("Pratiche aperte", "Open cases", "Offene Fälle")} value={stats.open_cases} tone="cyan" testid="stat-open" />
        <StatCard icon={ShieldQuestion} label={L("In attesa di verifica", "Awaiting verification", "Warten auf Verifizierung")} value={stats.awaiting_verification} tone="gold" testid="stat-verify" />
        <StatCard icon={Cpu} label={L("In analisi tecnica", "Under technical analysis", "In technischer Analyse")} value={stats.under_assessment} tone="slate" />
        <StatCard icon={Activity} label={L("Recuperi attivi", "Active recoveries", "Aktive Wiederherstellungen")} value={stats.active_recovery} tone="cyan" />
        <StatCard icon={CheckCircle2} label={L("Recuperi riusciti", "Successful recoveries", "Erfolgreiche Wiederherstellungen")} value={stats.successful} tone="green" />
        <StatCard icon={XCircle} label={L("Non riusciti", "Unsuccessful", "Nicht erfolgreich")} value={stats.unsuccessful} tone="red" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileText} label={L("Documenti in attesa", "Pending documents", "Ausstehende Dokumente")} value={stats.pending_documents} tone="slate" />
        <StatCard icon={FileText} label={L("Preventivi in attesa", "Pending quotes", "Ausstehende Angebote")} value={stats.pending_quotes} tone="gold" />
        <StatCard icon={FileText} label={L("Contratti in attesa", "Pending contracts", "Ausstehende Verträge")} value={stats.pending_contracts} tone="gold" />
        <StatCard icon={Receipt} label={L("Fatture aperte", "Open invoices", "Offene Rechnungen")} value={stats.open_invoices} tone="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-heading text-lg text-white mb-4">{L("Pratiche per stato", "Cases by status", "Fälle nach Status")}</h3>
          {stats.by_status.length === 0 ? <EmptyState icon={FolderOpen} title={L("Nessun dato", "No data", "Keine Daten")} /> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.by_status} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="label" stroke="#64748b" fontSize={10} width={140} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
                <Bar dataKey="count" fill="#00B4D8" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="p-6">
          <h3 className="font-heading text-lg text-white mb-4">{L("Pratiche per wallet", "Cases by wallet", "Fälle nach Wallet")}</h3>
          {stats.by_wallet.length === 0 ? <EmptyState icon={FolderOpen} title={L("Nessun dato", "No data", "Keine Daten")} /> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={stats.by_wallet} dataKey="count" nameKey="wallet" cx="50%" cy="50%" outerRadius={100} label>
                  {stats.by_wallet.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="font-heading text-lg text-white mb-4">{L("Attività recente", "Recent activity", "Letzte Aktivität")}</h3>
        {stats.recent_activity.length === 0 ? <p className="text-sm text-slate-500">{L("Nessuna attività.", "No activity.", "Keine Aktivität.")}</p> : (
          <div className="space-y-1.5">
            {stats.recent_activity.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm border-b border-white/5 py-2">
                <span className="text-slate-300"><span className="text-cyanx">{a.action}</span> · {a.entity_type}</span>
                <span className="text-xs text-slate-600">{a.actor_email} · {formatDateTime(a.timestamp)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
